import { Q } from "@nozbe/watermelondb"
import { map } from "rxjs"

import { PROGRESS_VERSION } from "@treefort/constants"
import { IKeyValueStore } from "@treefort/lib/types/settings"
import { keyToId } from "@treefort/lib/watermelon-key-to-id"

import { getClient } from "../db"
import { Progress, ProgressData } from "../models/progress"
import { KeyValueStore } from "./key-value"

type ProgressOptions = {
  profileId: string | null
}

const CURRENT_AUDIO_PROGRESS_KEY = `currentProgressKey.${PROGRESS_VERSION}`

const getProfileCurrentAudioKey = (profileId: string | null) =>
  `${CURRENT_AUDIO_PROGRESS_KEY}:${profileId || ""}`

class ProgressStore extends KeyValueStore<Progress> implements IKeyValueStore {
  constructor() {
    super(Progress.table)
  }

  async getCurrentAudioProgressItemKey({ profileId }: ProgressOptions) {
    return await getClient().localStorage.get<string>(
      getProfileCurrentAudioKey(profileId),
    )
  }

  async saveCurrentAudioProgressItemKey({
    profileId,
    value,
  }: {
    profileId: string | null
    value: string
  }) {
    await getClient().localStorage.set(
      getProfileCurrentAudioKey(profileId),
      value,
    )
  }

  async clearCurrentAudioProgressItemKey({ profileId }: ProgressOptions) {
    await getClient().localStorage.remove(getProfileCurrentAudioKey(profileId))
  }

  async getAllByCurrentUser<TProgressItemData>({ profileId }: ProgressOptions) {
    const userId = this.getUserId()

    const progressRecords = await getClient()
      .get<Progress>(Progress.table)
      .query(Q.where("userId", userId), Q.where("profileId", profileId || null))

    return progressRecords.map((p) => {
      try {
        return {
          timestamp: p.updatedAtDate,
          value: JSON.parse(p.data) as TProgressItemData,
        }
      } catch {
        return null
      }
    })
  }

  /**
   * Returns an observable that emits a list of all the user's progress, sorted
   * by `updatedAtDate`. Any time one of the items is add, removed, or updated,
   * a new list will be emitted.
   */
  observeAllByCurrentUser({
    profileId,
    limit,
    orderBy = ["updatedAtDate", "desc"],
  }: ProgressOptions & {
    limit?: number
    orderBy?: [keyof ProgressData, "desc" | "asc"]
  }) {
    const userId = this.getUserId()

    return getClient()
      .get<Progress>(Progress.table)
      .query([
        Q.where("userId", userId),
        Q.where("profileId", profileId || null),
        ...(typeof limit === "number" ? [Q.take(limit)] : []),
        Q.sortBy(...orderBy),
      ])
      .observeWithColumns(["updatedAtDate"])
  }

  observeSingle(
    key: string,
    { userId, profileId }: { userId: string; profileId: string | null },
  ) {
    return (
      getClient()
        .get<Progress>(Progress.table)
        // Note: we're not using .findAndObserve here because that throws
        // an error if the item does not already exist (which can happen
        // if the item is removed from library then re-added).
        .query(
          Q.where(
            "id",
            keyToId({
              collection: Progress.table,
              key,
              userId,
              profileId,
            }),
          ),
          Q.take(1),
        )
        .observeWithColumns(["updatedAtDate"])
        .pipe(map((arr) => (arr.length ? arr[0] : undefined)))
    )
  }

  observeCount() {
    return getClient().get<Progress>(Progress.table).query().observeCount(true)
  }
}

export const progressStore = new ProgressStore()
